import { useTranslation } from 'react-i18next';
import { formatDateTime } from '@/utils/date';
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  Close as CloseIcon,
  Work as WorkIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { decodeToken } from '@/utils/token';
import { useAppSelector } from '@/store/hooks';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Typography,
  IconButton,
  Stack,
  Box,
} from '@gapartners/ui';
import type { User } from '@/types/user';
import type { Manager } from '@/types';

export type PopUpUser = {
  popupUser:
    | (User & { isManager?: boolean })
    | (Manager & { isManager?: boolean });
  user: (User & { isManager?: boolean }) | null;
  onClose: () => void;
};

type NormalizedProfileUser = {
  name: string;
  email: string;
};

function normalizeProfileUser(user: User | Manager): NormalizedProfileUser {
  if ('mail' in user) {
    return {
      name: user.displayName ?? '',
      email: user.mail ?? '',
    };
  }

  return {
    name: user.name ?? '',
    email: user.email ?? '',
  };
}

/** First usable phone for tel: links (managers from Graph). */
function normalizeProfilePhone(user: User | Manager): string | null {
  if (!('mobilePhone' in user) && !('businessPhones' in user)) {
    return null;
  }
  const m = user as Manager;
  const mobile = m.mobilePhone?.trim();
  if (mobile) return mobile;
  const fromList = m.businessPhones?.map(p => p?.trim()).find(Boolean);
  return fromList ?? null;
}

type DetailRowProps = {
  icon: React.ReactNode;
  label: string;
  value: string;
};

function DetailRow({ icon, label, value }: DetailRowProps) {
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      {icon}
      <Typography variant="body2">
        {label}: <Box component="strong">{value}</Box>
      </Typography>
    </Stack>
  );
}

export function ProfilePopup({ popupUser, onClose }: PopUpUser) {
  const { t } = useTranslation();
  const { token } = useAppSelector(state => state.auth);
  const decodedToken = token ? decodeToken(token) : null;

  const lastLoginDate =
    typeof decodedToken?.auth_time === 'number'
      ? new Date(decodedToken.auth_time * 1000)
      : typeof decodedToken?.iat === 'number'
        ? new Date(decodedToken.iat * 1000)
        : null;

  const jobTitle =
    'jobTitle' in popupUser
      ? popupUser.jobTitle
      : t('pages.myProfile.agentJobTitle');

  const { name: displayName, email } = normalizeProfileUser(popupUser);
  const phone = normalizeProfilePhone(popupUser);
  const emailTrimmed = email.trim();
  const mailtoHref = emailTrimmed ? `mailto:${emailTrimmed}` : undefined;
  const telHref = phone ? `tel:${phone.replace(/\s/g, '')}` : undefined;

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box />
        <IconButton
          aria-label="close"
          onClick={onClose}
          size="small"
          sx={{ ml: 'auto' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 0 }}>
        <Stack direction="row" spacing={2} mb={3}>
          <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main' }}>
            {displayName[0]}
          </Avatar>
          <Stack>
            <Typography variant="subtitle1" fontWeight={600}>
              {displayName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {email}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {jobTitle}
            </Typography>
          </Stack>
        </Stack>

        <DialogActions sx={{ px: 0, pt: 0 }}>
          <IconButton
            color="primary"
            size="small"
            aria-label={
              mailtoHref
                ? t('pages.myProfile.openEmailAria', { email: emailTrimmed })
                : t('pages.myProfile.openEmailUnavailableAria')
            }
            {...(mailtoHref
              ? { component: 'a' as const, href: mailtoHref }
              : { disabled: true })}
          >
            <EmailIcon />
          </IconButton>
          <IconButton
            color="primary"
            size="small"
            aria-label={
              telHref
                ? t('pages.myProfile.openPhoneAria', { phone: phone ?? '' })
                : t('pages.myProfile.openPhoneUnavailableAria')
            }
            {...(telHref
              ? { component: 'a' as const, href: telHref }
              : { disabled: true })}
          >
            <PhoneIcon />
          </IconButton>
        </DialogActions>

        <Stack spacing={1.5} sx={{ mt: 2 }}>
          <DetailRow
            icon={<WorkIcon sx={{ fontSize: 18, color: 'action.active' }} />}
            label={t('pages.myProfile.jobTitle')}
            value={jobTitle ?? ''}
          />
          {!popupUser.isManager && (
            <DetailRow
              icon={
                <ScheduleIcon sx={{ fontSize: 18, color: 'action.active' }} />
              }
              label={t('pages.myProfile.lastLogin')}
              value={
                lastLoginDate
                  ? formatDateTime(lastLoginDate)
                  : (t('common.notAvailable') ?? '')
              }
            />
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
